import { useEffect } from 'react';

/**
 * ★ **시스템 「이전」이 시트를 닫는다** (2026-09-10 실기 · Android PWA).
 *
 * ── 왜 있는가 ────────────────────────────────────────────────
 *
 * 노트북에서 ESC 는 리모컨 엔진의 뒤로 키다 — 맨 위 층의 닫기 버튼(`data-focus-back`)을 누른다
 * (`tv-focus/decide.ts` · 절대 규칙 30 의 그 예외). Android 의 「이전」은 키가 아니라 **히스토리
 * 뒤로가기**라 그 엔진에 닿지 않고, 스택이 비어 있으면 **앱이 닫힌다** — 편성표를 보다 「이전」을
 * 누르면 앱 밖이었다.
 *
 * ── 어떻게 ──────────────────────────────────────────────────
 *
 * 층(`[data-focus-scope]`)이 **하나라도 서는 순간** 히스토리 항목 하나를 넣어 둔다(주소는 그대로).
 * 「이전」이 그 항목을 걷으면(`popstate`) **엔진과 같은 규칙**으로 맨 위 층(DOM 순서의 마지막)의
 * `data-focus-back` 을 누른다 — ESC 와 정확히 같은 길이고, 닫힌 뒤의 포커스 치유도 그 버튼의
 * `onClose` 가 갖는다. 층이 **닫기 버튼으로** 닫히면 우리가 넣은 항목을 `history.back()` 으로
 * 거둔다(그 `popstate` 는 건너뛴다) — 안 거두면 다음 「이전」이 빈 항목 하나를 먹고 아무 일도
 * 안 한다.
 *
 * **층의 목록을 두지 않는다** — 화면에 그려진 `[data-focus-scope]` 에서 파생한다(절대 규칙 29 의
 * 태도 · `MutationObserver`). 시트마다 훅을 두면 「설정을 닫고 편성표를 여는」 한 핸들러에서 두
 * 항목이 엇갈린다. **항목은 언제나 하나다** — 층이 0 ↔ 1+ 를 오갈 때만 넣고 거둔다.
 *
 * **기본 층은 대상이 아니다.** 시청 화면의 「홈」(`data-focus-back` · 층 밖)은 이 경로로 눌리지
 * 않는다 — 층이 없으면 항목도 없어 「이전」은 지금처럼 앱을 나간다(요청 범위: 시트 둘).
 *
 * TV·PC 에는 해가 없다 — 엔진이 뒤로 키를 `preventDefault` 로 삼키므로 히스토리는 움직이지 않고,
 * 어떤 TV 브라우저가 뒤로 키를 히스토리로 보내면 그때도 같은 닫기다.
 *
 * ── 판정은 순수하다 ─────────────────────────────────────────
 *
 * 이 패키지는 node 환경에서 테스트한다(jsdom 미도입). 넣고·거두고·건너뛰는 규칙은 아래 두 순수
 * 함수이고 DOM·`history` 를 만지는 것은 `useBrowserBack` 하나다.
 */
export const LAYER_STATE_KEY = 'bloomLayer';

export type LayerAction = 'push' | 'pop' | 'none';

/**
 * 층의 수가 바뀌었을 때 히스토리에 무엇을 하는가.
 *
 * - 0 → 1+ 이고 우리 항목이 없다 → `push`
 * - 1+ → 0 이고 우리 항목이 **아직 스택 위에 있다** → `pop` (닫기 버튼으로 닫혔다)
 * - 그 밖 → `none` (층끼리의 교체 · 「이전」으로 이미 걷힌 뒤의 닫힘)
 */
export function decideOnLayers(input: {
  readonly before: number;
  readonly after: number;
  /** 우리가 넣은 항목이 살아 있다고 믿는가 */
  readonly pushed: boolean;
  /** `history.state` 가 우리 것인가 — 「이전」이 이미 걷었으면 거짓 */
  readonly entryOnTop: boolean;
}): LayerAction {
  if (input.before === 0 && input.after > 0 && !input.pushed) return 'push';
  if (input.before > 0 && input.after === 0 && input.pushed && input.entryOnTop) return 'pop';
  return 'none';
}

export type PopAction = 'skip' | 'close' | 'none';

/**
 * `popstate` 에 무엇을 하는가.
 *
 * - 우리가 부른 `history.back()` 의 것 → `skip`
 * - 층이 있고 닫기 버튼이 있다 → `close` (그 버튼을 누른다 — ESC 와 같은 길)
 * - 그 밖(층 없음 · 닫기 없는 층) → `none`
 */
export function decideOnPop(input: {
  readonly suppressed: boolean;
  readonly hasBackButton: boolean;
}): PopAction {
  if (input.suppressed) return 'skip';
  return input.hasBackButton ? 'close' : 'none';
}

function layerCount(): number {
  return document.querySelectorAll('[data-focus-scope]').length;
}

/** 맨 위 층의 닫기 버튼 — **엔진과 같은 규칙**(DOM 순서의 마지막 층 · `use-tv-focus.ts` 의 `scopeRoot`) */
function topBackButton(): HTMLElement | null {
  const scopes = document.querySelectorAll<HTMLElement>('[data-focus-scope]');
  const top = scopes[scopes.length - 1];
  return top?.querySelector<HTMLElement>('[data-focus-back]') ?? null;
}

function entryOnTop(): boolean {
  const state: unknown = window.history.state;
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[LAYER_STATE_KEY] === true
  );
}

/** DOM 과 `history` 를 만지는 자리는 여기 하나다. `App` 이 한 번 건다. */
export function installBrowserBack(): () => void {
  let pushed = entryOnTop();
  let last = layerCount();
  let suppressed = 0;

  const sync = () => {
    const now = layerCount();
    const action = decideOnLayers({ before: last, after: now, pushed, entryOnTop: entryOnTop() });
    last = now;
    if (action === 'push') {
      window.history.pushState({ [LAYER_STATE_KEY]: true }, '');
      pushed = true;
    } else if (action === 'pop') {
      pushed = false;
      suppressed += 1;
      window.history.back();
    } else if (now === 0) {
      pushed = false;
    }
  };
  const observer = new MutationObserver(sync);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-focus-scope'],
  });

  const onPop = () => {
    const back = topBackButton();
    const action = decideOnPop({ suppressed: suppressed > 0, hasBackButton: back !== null });
    if (action === 'skip') {
      suppressed -= 1;
      return;
    }
    // 「이전」이 우리 항목을 걷었다 — 다음 층이 서면 다시 넣는다.
    pushed = false;
    if (action === 'close' && back !== null) {
      back.click();
      /*
       * 닫기 버튼을 **탭**해 닫으면 그 탭이 포커스를 닫기 버튼으로 옮겼다가 언마운트와 함께 `<body>`
       * 로 떨어진다. 「이전」은 탭이 없어 포커스가 **시트를 연 버튼에 그대로** 남고, 터치 기기에서는
       * 그것이 눌린 채로 보였다(2026-09-10 실기). 거친 포인터에서만 같은 결과로 맞춘다 — 리모컨은
       * 포커스가 곧 위치라 비우지 않는다(다음 키에 엔진이 되돌린다 · 규칙 32).
       */
      if (window.matchMedia('(pointer: coarse)').matches) {
        const active = document.activeElement;
        if (active instanceof HTMLElement) active.blur();
      }
    }
  };
  window.addEventListener('popstate', onPop);

  return () => {
    observer.disconnect();
    window.removeEventListener('popstate', onPop);
  };
}

export function useBrowserBack(): void {
  useEffect(() => installBrowserBack(), []);
}
